import React from "react";

/** Lignes « récap fiche de poste » — partagées entre l’étape Confirmation (création) et le modal d’aperçu. */

const Row = ({ label, value }) => (
    <div className={"flex justify-between items-start gap-4 py-4 border-b border-gray-100"}>
        <div className={"ft-sm text-gray-500 shrink-0"}>{label}</div>
        <div
            className={
                "ft-sm text-gray-900 text-right break-words max-w-[70%] min-w-0"
            }
        >
            {value || "—"}
        </div>
    </div>
);

export const formatList = (list) =>
    Array.isArray(list) && list.length > 0 ? list.join(", ") : "";

export const formatAddress = (location) => {
    if (!location) return "";
    if (typeof location === "string") return location;
    const { street, postcode, city, department } = location;
    return [street, postcode, city, department].filter(Boolean).join(" ");
};

export const formatSalaryApi = (t) => {
    const wage =
        typeof t?.salary?.wage_per_hour === "number"
            ? t.salary.wage_per_hour
            : null;
    if (wage === null || Number.isNaN(wage)) return "";
    const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(wage);
    const suffix = t.isIndexedToMinimumWage ? " (indexé au SMIC)" : "";
    return `${formatted} € par heure${suffix}`;
};

/**
 * Formulaire assistant (templateData) — étape « Confirmation ».
 */
export const buildSummaryFromWizardData = (templateData) => ({
    name: templateData?.name,
    activity: templateData?.activity,
    qualification: templateData?.qualification,
    tools: formatList(templateData?.tools),
    licences: formatList(templateData?.licences),
    languages: formatList(templateData?.languages),
    address: formatAddress(templateData?.location),
    salary:
        templateData?.salary !== undefined && templateData?.salary !== ""
            ? `${templateData.salary} € par heure${
                  templateData.isIndexedToMinimumWage
                      ? " (indexé au SMIC)"
                      : ""
              }`
            : "",
    contact:
        templateData?.contact_name
            ? `${templateData.contact_name}${
                  templateData.contact_poste
                      ? ` — ${templateData.contact_poste}`
                      : ""
              }`
            : "",
    other: templateData?.other,
    otherInterim: templateData?.other_interim,
});

/**
 * Document API / liste (getMissionTemplates) — même champs affichés que le formulaire.
 */
export const buildSummaryFromApiTemplate = (template) => ({
    name: template?.name || template?.title,
    activity: template?.activity,
    qualification: template?.qualification || template?.title,
    tools: formatList(template?.tools),
    licences: formatList(template?.licences),
    languages: formatList(template?.language || template?.languages),
    address: formatAddress(template?.location),
    salary: formatSalaryApi(template),
    contact: template?.contact?.name
        ? `${template.contact.name}${
              template.contact.poste ? ` — ${template.contact.poste}` : ""
          }`
        : "",
    other: template?.other,
    otherInterim: template?.other_interim,
});

/**
 * Tableau de lignes label / valeur (identique au bloc sous « Confirmation » dans le wizard).
 */
export const MissionTemplateSummaryRows = ({
    summary,
    smicWarning = false,
}) => {
    if (!summary) return null;
    return (
        <div className={"flex flex-col"}>
            <Row label={"Intitulé de la fiche"} value={summary.name} />
            <Row label={"Secteur"} value={summary.activity} />
            <Row label={"Qualification"} value={summary.qualification} />
            <Row label={"Matériel"} value={summary.tools} />
            <Row label={"Permis"} value={summary.licences} />
            <Row label={"Langues"} value={summary.languages} />
            <Row label={"Adresse"} value={summary.address} />
            <Row label={"Taux horaire brut"} value={summary.salary} />
            <Row label={"Responsable"} value={summary.contact} />
            <Row label={"Notes intérimaire"} value={summary.other} />
            <Row label={"Notes agence"} value={summary.otherInterim} />
            {smicWarning && (
                <p className="mt-4 ft-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    Cette fiche est inférieure au SMIC. Mettez-la à jour avant de
                    l&apos;utiliser pour créer une mission.
                </p>
            )}
        </div>
    );
};

/**
 * Point d’entrée unique : confirmation (wizard) ou modal (document API).
 * Passez **soit** `wizardData` **soit** `apiTemplate` — pas les deux.
 */
const MissionTemplateSummary = ({
    wizardData,
    apiTemplate,
    smicWarning = false,
}) => {
    const summary =
        wizardData != null
            ? buildSummaryFromWizardData(wizardData)
            : buildSummaryFromApiTemplate(apiTemplate);

    return (
        <MissionTemplateSummaryRows summary={summary} smicWarning={smicWarning} />
    );
};

export default MissionTemplateSummary;
